import type {
  DocumentMetadataUserEditEntry,
  DocumentMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { cantileverDocumentEntryId } from 'features/knowledgeDocumentViewer/utils/cantileverHostIds'
import { documentMetadataUserEditEntryId } from 'features/knowledgeDocumentViewer/utils/metadataUserEditIds'
import {
  appendEditLog,
  buildDocumentMetadataEditLogEntry,
  buildDocumentMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/utils/metadataUserEditWrite'
import { type RolodexEntryPutJson, RolodexEntryType } from 'types'

export type DocumentMetadataUserEditPutData = {
  document_id: string
  system_document_entry_id: string | null
  values: DocumentMetadataUserEditValues
  edit_log: NonNullable<
    DocumentMetadataUserEditEntry['entry']['data']['edit_log']
  >
}

export type BuildDocumentMetadataUserEditPutArgs = {
  siteId: string
  organizationId: string
  bqDocumentId: string
  editedBy: string
  editedAt: string
  systemDocument: SystemDocumentEntry | undefined
  priorEdit: DocumentMetadataUserEditEntry | undefined
  userValues: DocumentMetadataUserEditValues
}

export function buildDocumentMetadataUserEditPut(
  args: BuildDocumentMetadataUserEditPutArgs
): RolodexEntryPutJson<DocumentMetadataUserEditPutData> {
  const {
    siteId,
    organizationId,
    bqDocumentId,
    editedBy,
    editedAt,
    systemDocument,
    priorEdit,
    userValues,
  } = args

  const entryId = documentMetadataUserEditEntryId(siteId, bqDocumentId)
  const priorValues = buildDocumentMetadataUserEditValues(
    priorEdit?.entry.data.values ?? {}
  )
  const values = {
    ...priorValues,
    ...buildDocumentMetadataUserEditValues(userValues),
  }
  const logRow = buildDocumentMetadataEditLogEntry({
    editedAt,
    editedBy,
    systemData: systemDocument?.entry.data,
    userValues: buildDocumentMetadataUserEditValues(userValues),
  })
  const editLog = appendEditLog(priorEdit?.entry.data.edit_log, logRow)

  return {
    id: entryId,
    type: RolodexEntryType.DOCUMENT_METADATA_USER_EDIT,
    data: {
      document_id: bqDocumentId,
      system_document_entry_id: systemDocument?.entry.id ?? null,
      values,
      edit_log: editLog,
    },
    tags: {
      organization: organizationId,
      site: siteId,
      'cantilever-document': cantileverDocumentEntryId(siteId, bqDocumentId),
    },
  }
}
